import { useState } from "react";
import { createPortal } from "react-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Info, X } from "lucide-react";
import logger from '@/lib/logger';

interface UserStatsTooltipProps {
  userProfile: any;
}

export function UserStatsTooltip({ userProfile }: UserStatsTooltipProps) {
  const [showStats, setShowStats] = useState(false);

  if (!userProfile) {
    logger.debug("UserStatsTooltip: No userProfile provided");
    return null;
  }

  logger.debug("UserStatsTooltip: userProfile data:", userProfile);

  const formatArrayValue = (value: any) => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "None";
    }
    return value || "Not specified";
  };

  return (
    <div className="mt-6 relative">
      <button 
        onClick={() => {
          logger.debug("View My Stats clicked, current state:", showStats);
          setShowStats(!showStats);
        }}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 transition-colors cursor-pointer"
      >
        <Info className="w-4 h-4" />
        <span className="text-sm font-medium">View My Stats</span>
      </button>
      
      {showStats && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-75"
            style={{ zIndex: 99998 }}
            onClick={() => setShowStats(false)}
          />
          {/* Modal */}
          <div 
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-4xl max-h-[90vh] overflow-y-auto"
            style={{ zIndex: 99999 }}
          >
            <Card className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Your Profile Stats
                  </h4>
                  <button 
                    onClick={() => setShowStats(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Personal Information */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h5 className="font-semibold text-green-600 mb-3 text-sm uppercase tracking-wider">Personal Info</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Height:</span>
                        <span className="font-medium">{userProfile.height || 'Not specified'} cm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Weight:</span>
                        <span className="font-medium">{userProfile.weight || 'Not specified'} kg</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Age:</span>
                        <span className="font-medium">{userProfile.age || 'Not specified'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Gender:</span>
                        <span className="font-medium capitalize">{userProfile.gender || 'Not specified'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Training Info */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h5 className="font-semibold text-green-600 mb-3 text-sm uppercase tracking-wider">Training</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Frequency:</span>
                        <span className="font-medium">{userProfile.trainingFrequency || 'Not specified'} days/week</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Jump Style:</span>
                        <span className="font-medium capitalize">{userProfile.jumpingStyle?.replace('_', ' ') || 'Not specified'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Equipment - Weights */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h5 className="font-semibold text-green-600 mb-3 text-sm uppercase tracking-wider">Weight Equipment</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Dumbbells:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.dumbbells)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Kettlebells:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.kettlebells)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Weight Plates:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.weightPlates)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Barbells:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.barbells)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Leg Press:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.legPressAccess)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Medicine Balls:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.medicineBalls)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Sandbags:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.sandbags)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Equipment - Training Tools */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h5 className="font-semibold text-green-600 mb-3 text-sm uppercase tracking-wider">Training Tools</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Jump Surfaces:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.jumpSurfaces)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Jump Rope:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.jumpRope)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Resistance Bands:</span>
                        <span className="font-medium">{userProfile.resistanceBands ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Agility Ladder:</span>
                        <span className="font-medium capitalize">{userProfile.agilityLadder || 'Not specified'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Farmers Carry:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.farmersCarry)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Sled Access:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.sledAccess)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Equipment - Facilities */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h5 className="font-semibold text-green-600 mb-3 text-sm uppercase tracking-wider">Facilities & Space</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Mobility Equipment:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.mobilityEquipment)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Rack Access:</span>
                        <span className="font-medium capitalize">{userProfile.rackAccess?.replace('_', ' ') || 'Not specified'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Court Access:</span>
                        <span className="font-medium capitalize">{userProfile.courtAccess?.replace('_', ' ') || 'Not specified'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Optional Stats */}
                  {(userProfile.standingVertical || userProfile.runningVertical || userProfile.armSpan || userProfile.squatMax || userProfile.deadliftMax) && (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <h5 className="font-semibold text-green-600 mb-3 text-sm uppercase tracking-wider">Performance Stats</h5>
                      <div className="space-y-2 text-sm">
                        {userProfile.standingVertical && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Standing Vertical:</span>
                            <span className="font-medium">{userProfile.standingVertical} cm</span>
                          </div>
                        )}
                        {userProfile.runningVertical && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Running Vertical:</span>
                            <span className="font-medium">{userProfile.runningVertical} cm</span>
                          </div>
                        )}
                        {userProfile.armSpan && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Arm Span:</span>
                            <span className="font-medium">{userProfile.armSpan} cm</span>
                          </div>
                        )}
                        {userProfile.squatMax && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Squat Max:</span>
                            <span className="font-medium">{userProfile.squatMax} kg</span>
                          </div>
                        )}
                        {userProfile.deadliftMax && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Deadlift Max:</span>
                            <span className="font-medium">{userProfile.deadliftMax} kg</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Injury History */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h5 className="font-semibold text-green-600 mb-3 text-sm uppercase tracking-wider">Health & Limitations</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Injury History:</span>
                        <span className="font-medium text-right">{formatArrayValue(userProfile.injuryHistory)}</span>
                      </div>
                      {userProfile.otherInjury && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Other Injuries:</span>
                          <span className="font-medium text-right">{userProfile.otherInjury}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}